import { AlertTriangle, CheckCircle2, CircleGauge, Pencil, ReceiptText, RefreshCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BankReconciliationSuggestion } from '../../api/client';
import { confidencePercent, isSuggestionExecutable } from './utils';
import DocumentLink from './DocumentLink';

interface Props {
  suggestion: BankReconciliationSuggestion;
  selectionEnabled?: boolean;
  analyzing?: boolean;
  selected: boolean;
  busy: boolean;
  executing?: boolean;
  overrideReason: string;
  onSelectedChange: (selected: boolean) => void;
  onOverrideReasonChange: (reason: string) => void;
  onEdit: () => void;
  onIgnore: () => void;
  onRestore: () => void;
  onExecute: () => void;
  onRefreshReceipt: () => void;
  refreshingReceipt?: boolean;
  receiptRefreshState?: 'checking' | 'not_found' | 'possible_match' | 'error';
  receiptRefreshError?: string;
  onOpenCustomer: (customerId: string, customerName: string) => void;
}

const confidenceStyle = {
  high: 'border-green-200 bg-green-50 text-green-800',
  medium: 'border-amber-200 bg-amber-50 text-amber-800',
  low: 'border-red-200 bg-red-50 text-red-800',
};

export default function SuggestionCard(props: Props) {
  const { suggestion } = props;
  const { t, i18n } = useTranslation();
  const bankPayerName = suggestion.transaction.payer_name?.trim();
  const customerName = suggestion.customer_id ? suggestion.customer_name?.trim() : undefined;
  const money = (value: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: suggestion.transaction.currency || 'ILS',
  }).format(value);
  const executable = isSuggestionExecutable(suggestion);
  const needsReason = (suggestion.receipt_matches?.length ?? 0) > 0
    || (suggestion.requires_override && !suggestion.material_edit_made);
  const outcome = suggestion.execution;
  const resolution = suggestion.receipt_resolution;
  const canRefreshReceipt = executable
    && !resolution
    && !outcome;
  const possibleReceipts = suggestion.receipt_matches?.filter(receipt =>
    !receipt.confirmed && receipt.receipt_id !== resolution?.receipt.receipt_id) ?? [];
  const savedReceipt = suggestion.receipt_matches?.find(receipt =>
    receipt.document_number === outcome?.receipt_document_number
    && (outcome.receipt_document_type === undefined || receipt.document_type === outcome.receipt_document_type));
  const outcomeStyle = outcome?.status === 'completed'
    ? 'border-green-200 bg-green-50 text-green-800'
    : outcome?.status === 'partial_success'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-red-200 bg-red-50 text-red-800';
  const OutcomeIcon = outcome?.status === 'completed' ? CheckCircle2 : outcome ? XCircle : ReceiptText;

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {props.selectionEnabled !== false && (
            <input
              type="checkbox"
              checked={props.selected}
              disabled={props.busy || !executable}
              onChange={(e) => props.onSelectedChange(e.target.checked)}
              aria-label={t('reconciliation.selectTransaction', { payer: bankPayerName || customerName || suggestion.transaction.reference || suggestion.suggestion_id })}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{bankPayerName || customerName || t('reconciliation.unknownPayer')}</h3>
            {!bankPayerName && <p className="text-xs text-amber-700">{t('reconciliation.bankPayerMissing')}</p>}
            <p className="text-sm text-gray-500">{suggestion.transaction.description || t('reconciliation.noMemo')}</p>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold ${confidenceStyle[suggestion.confidence.level]}`}>
          {suggestion.confidence.level === 'high' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {t(`reconciliation.confidence.${suggestion.confidence.level}`)} · {confidencePercent(suggestion.confidence.score)}%
        </div>
      </div>

      {resolution && suggestion.status === 'resolved' && (
        <div role="status" className={`mt-3 rounded-lg border-2 p-3 text-sm ${resolution.stale
          ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-green-300 bg-green-50 text-green-900'}`}>
          <p className="font-semibold">{t(resolution.stale
            ? 'reconciliation.receiptResolution.stale' : 'reconciliation.receiptResolution.resolved')}</p>
          <p className="mt-2">
            <DocumentLink kind="receipt" documentType={resolution.receipt.document_type}
              documentNumber={resolution.receipt.document_number} documentDate={resolution.receipt.receipt_date} />
            {' · '}<time dateTime={resolution.receipt.receipt_date} dir="ltr" className="inline-block whitespace-nowrap">{resolution.receipt.receipt_date}</time>
            {' · '}{money(resolution.receipt.amount)}
          </p>
          {resolution.basis === 'grouped_amount_date_assumed' && (
            <p className="mt-1">
              {t('reconciliation.receiptResolution.grouped', {
                count: resolution.group_suggestion_ids?.length ?? 0,
                amount: money(resolution.group_total ?? resolution.receipt.amount),
              })}
            </p>
          )}
          <p className="mt-1">{t('reconciliation.receiptResolution.noFiscalChanges')}</p>
        </div>
      )}

      {possibleReceipts.length > 0 && (
        <div role="alert" className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />{t('reconciliation.possibleReceipt')}</p>
          <p className="mt-1">{t('reconciliation.documentLinks.checkReceipt')}</p>
          {!suggestion.customer_confirmed_by && (suggestion.customer_identity_confidence ?? 0) < 0.85
            && <p className="mt-1">{t('reconciliation.receiptResolution.confirmCustomer')}</p>}
          {possibleReceipts.map((receipt) => (
            <p key={receipt.receipt_id} className="mt-2">
              <DocumentLink kind="receipt" documentType={receipt.document_type} documentNumber={receipt.document_number} documentDate={receipt.receipt_date} />
              {' · '}<time dateTime={receipt.receipt_date} dir="ltr" className="inline-block whitespace-nowrap">{receipt.receipt_date}</time>
              {' · '}{money(receipt.amount)}
            </p>
          ))}
        </div>
      )}

      {outcome && (
        <div role="status" className={`mt-3 rounded-lg border p-3 text-sm ${outcomeStyle}`}>
          <div className="flex items-center gap-2 font-semibold">
            <OutcomeIcon className="h-4 w-4" aria-hidden="true" />
            {t(outcome.reused_existing_receipt && outcome.status === 'completed'
              ? 'reconciliation.outcome.alreadyCreated'
              : `reconciliation.outcome.${outcome.status}`)}
          </div>
          {outcome.reused_existing_receipt && outcome.status !== 'completed' && <p>{t('reconciliation.outcome.alreadyCreated')}</p>}
          {outcome.receipt_document_number && <p className="mt-1">
            <DocumentLink kind="receipt" documentType={outcome.receipt_document_type ?? savedReceipt?.document_type ?? suggestion.payment.receipt_type}
              documentNumber={outcome.receipt_document_number} documentDate={savedReceipt?.receipt_date} />
            {savedReceipt && <>{' · '}<time dateTime={savedReceipt.receipt_date} dir="ltr" className="inline-block whitespace-nowrap">{savedReceipt.receipt_date}</time></>}
          </p>}
          {outcome.error_message && <p>{outcome.error_message}</p>}
        </div>
      )}

      {props.analyzing && (
        <p role="status" className="mt-3 text-xs font-medium text-blue-700">
          {t('reconciliation.progressive.liveCard')}
        </p>
      )}
      {props.receiptRefreshState && (
        <p
          role={props.receiptRefreshState === 'error' ? 'alert' : 'status'}
          className={`mt-3 text-sm ${
            props.receiptRefreshState === 'error'
              ? 'text-red-700'
              : props.receiptRefreshState === 'not_found'
                ? 'text-gray-600'
                : 'text-blue-700'
          }`}
        >
          {props.receiptRefreshState === 'error'
            ? props.receiptRefreshError || t('reconciliation.receiptResolution.refreshError')
            : t(`reconciliation.receiptResolution.${props.receiptRefreshState}`)}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div><dt className="text-gray-500">{t('reconciliation.bankDate')}</dt><dd className="font-medium">{suggestion.transaction.transaction_date}</dd></div>
        <div><dt className="text-gray-500">{t('reconciliation.reference')}</dt><dd className="font-medium">{suggestion.transaction.reference || '—'}</dd></div>
        <div><dt className="text-gray-500">{t('reconciliation.amount')}</dt><dd className="font-semibold">{money(suggestion.transaction.amount)}</dd></div>
        <div>
          <dt className="text-gray-500">{t('reconciliation.customer')}</dt>
          <dd className="font-medium">
            {suggestion.customer_id && customerName ? (
              <button
                type="button"
                onClick={() => props.onOpenCustomer(suggestion.customer_id!, customerName)}
                className="text-primary-700 underline decoration-primary-300 underline-offset-2 hover:text-primary-900"
              >
                {customerName}
              </button>
            ) : t('reconciliation.unmatched')}
          </dd>
        </div>
      </dl>

      <div className="mt-4 rounded-lg bg-gray-50 p-3">
        <h4 className="text-sm font-semibold text-gray-800">{t('reconciliation.allocations')}</h4>
        <p className="mt-1 text-xs text-gray-600">{t('reconciliation.allocationPlanHelp')}</p>
        <div className="mt-2 space-y-2">
          {suggestion.allocations.map((allocation) => (
            <div key={`${allocation.document_type}:${allocation.document_number}`} className="flex flex-wrap justify-between gap-2 text-sm">
              <span><DocumentLink kind="invoice" documentType={allocation.document_type} documentNumber={allocation.document_number} documentDate={allocation.document_date} /> · <time dateTime={allocation.document_date} dir="ltr" className="inline-block whitespace-nowrap">{allocation.document_date}</time> · {allocation.amount === allocation.open_balance ? t('reconciliation.full') : t('reconciliation.partial')}</span>
              <span className="font-medium">{money(allocation.amount)} · {t('reconciliation.remaining')} {money(allocation.remaining_balance)}</span>
            </div>
          ))}
          {suggestion.allocations.length === 0 && <p className="text-sm text-gray-500">{t('reconciliation.noAllocations')}</p>}
          <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-medium">
            <span>{t('reconciliation.unallocated')}</span><span>{money(suggestion.unallocated_amount)}</span>
          </div>
        </div>
      </div>

      {suggestion.status === 'edited' && !outcome && !resolution && !props.executing && (
        <p role="status" className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
          {t('reconciliation.changesSaved')}
        </p>
      )}

      <p className="mt-3 flex items-start gap-2 text-sm text-gray-600">
        <CircleGauge className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {suggestion.confidence.reason}
      </p>
      {needsReason && executable && (
        <label className="mt-3 block text-sm font-medium text-gray-700">
          {t('reconciliation.overrideReason')}
          <textarea value={props.overrideReason} onChange={(e) => props.onOverrideReasonChange(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-amber-300 px-3 py-2 focus:border-amber-500 focus:ring-amber-500" />
          <span className="mt-1 block text-xs text-amber-700">{t('reconciliation.overrideHelp')}</span>
        </label>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {suggestion.status === 'ignored' ? (
          <button type="button" onClick={props.onRestore} disabled={props.busy} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium">{t('reconciliation.restore')}</button>
        ) : executable ? (
          <>
            {canRefreshReceipt && (
              <button
                type="button"
                onClick={props.onRefreshReceipt}
                disabled={props.busy}
                className="inline-flex items-center gap-1 rounded-lg border border-primary-300 px-3 py-2 text-sm font-medium text-primary-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${props.refreshingReceipt ? 'animate-spin' : ''}`} aria-hidden="true" />
                {t('reconciliation.receiptResolution.refreshSuggestion')}
              </button>
            )}
            <button type="button" onClick={props.onIgnore} disabled={props.busy} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700">{t('reconciliation.ignore')}</button>
            <button type="button" onClick={props.onEdit} disabled={props.busy} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700">
              <Pencil className="h-4 w-4" aria-hidden="true" />{t('common.edit')}
            </button>
            <button type="button" onClick={props.onExecute} disabled={props.busy || (needsReason && props.overrideReason.trim().length < 3)} className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
              {props.executing ? t('reconciliation.executing') : t('reconciliation.execute')}
            </button>
          </>
        ) : null}
      </div>
    </article>
  );
}
